import type { ReportTemplate } from '../types/reportTemplate';

/**
 * Pick the template that should drive viewer/export rendering when a report
 * does not carry an explicit `templateId`.
 *
 * Priority:
 *  1. report-linked template id
 *  2. active template id from the current session
 *  3. default template (`isDefault === true`)
 *  4. no template
 */
export function resolvePreferredTemplate(
  templates: ReportTemplate[],
  opts?: {
    reportTemplateId?: string | null;
    activeTemplateId?: string | null;
  },
): ReportTemplate | undefined {
  const reportTemplateId = opts?.reportTemplateId ?? null;
  if (reportTemplateId) {
    const linked = templates.find(t => t.id === reportTemplateId);
    if (linked) return linked;
    // Report explicitly links to this id — do not substitute active/default.
    return undefined;
  }

  const activeTemplateId = opts?.activeTemplateId ?? null;
  if (activeTemplateId) {
    const active = templates.find(t => t.id === activeTemplateId);
    // William QA (2026-06-17) — an archetype SEED (read-only fork starting point,
    // id `archetype-*`) is NOT an explicit template choice. Don't let an
    // auto-selected seed hijack the no-explicit-template path: fall through to a
    // real isDefault template, else the stock-default masthead — so "no template
    // assigned" renders the new masthead look, not the old hero-image seed cover.
    // An explicitly FORKED archetype template (a user/company copy with a
    // different id) still wins here.
    if (active && !active.id.startsWith('archetype-')) return active;
  }

  const personalDefault = templates.find(t => t.isDefault && t.scope === 'user');
  if (personalDefault) return personalDefault;

  const companyDefault = templates.find(t => t.isDefault && t.scope === 'company');
  if (companyDefault) return companyDefault;

  return templates.find(t => t.isDefault);
}

export interface ReportTemplateStamp {
  templateId?: string;
  templateVersionUsed?: number;
}

/**
 * Stamp `templateId` / `templateVersionUsed` onto a freshly generated report.
 * Reruns inherit the parent report's template link; new reports link to the
 * active/default template.
 */
export function resolveReportTemplateStamp(
  templates: ReportTemplate[],
  opts: {
    parentTemplateId?: string | null;
    parentTemplateVersionUsed?: number;
    activeTemplateId?: string | null;
  },
): ReportTemplateStamp {
  const preferred = resolvePreferredTemplate(templates, {
    reportTemplateId: opts.parentTemplateId,
    activeTemplateId: opts.activeTemplateId,
  });

  const parentId = opts.parentTemplateId?.trim();
  if (parentId) {
    return {
      templateId: parentId,
      templateVersionUsed: preferred?.version ?? opts.parentTemplateVersionUsed,
    };
  }

  // Don't STAMP an archetype seed onto a freshly generated report — seeds are
  // fork starting points, not explicit choices, so an unstamped report resolves
  // to the stock-default masthead. (resolvePreferredTemplate already skips seeds,
  // so this is belt-and-suspenders for any direct seed `preferred`.)
  if (preferred && !preferred.id.startsWith('archetype-')) {
    return {
      templateId: preferred.id,
      templateVersionUsed: preferred.version,
    };
  }

  return {};
}
